import { computed, type ComputedRef, type Ref } from 'vue'

export interface StarData {
  weight: 'fill' | 'regular'
  partial?: boolean
  fillPercentage?: number
}

/**
 * Calculates star data array for displaying star ratings
 * @param rating - The rating value (0-5), supports decimals for partial ratings
 * @returns Array of 5 star objects with display properties
 */
export function calculateStars(rating: number | null | undefined): StarData[] {
  // Handle case where rating is undefined or null
  if (rating == null) {
    return Array(5).fill({ weight: 'regular' })
  }

  const clampedRating = Math.max(0, Math.min(5, rating)) // Clamp between 0 and 5
  const fullStars = Math.floor(clampedRating)
  const remainder = clampedRating - fullStars
  const hasPartialStar = remainder > 0
  const emptyStars = 5 - fullStars - (hasPartialStar ? 1 : 0)

  const stars: StarData[] = []

  // Add full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push({ weight: 'fill' })
  }

  // Add partial star if needed
  if (hasPartialStar) {
    stars.push({
      fillPercentage: remainder * 100,
      partial: true,
      weight: 'regular',
    })
  }

  // Add empty stars
  for (let i = 0; i < emptyStars; i++) {
    stars.push({ weight: 'regular' })
  }

  return stars
}

/**
 * Composable for star rating functionality
 * @param rating - Reactive rating value
 */
export function useStarRating(
  rating:
    | Ref<number | null | undefined>
    | ComputedRef<number | null | undefined>
) {
  const stars = computed(() => calculateStars(rating.value))

  return {
    stars,
  }
}
